import classNames from 'classnames';
import { OrgTreeSelector } from '../org-tree-selector/org-tree-selector';
import { ObjectPillListItem, ObjectPillList } from '../pill-list';
import { ErrorMessage } from '../error-message/error-message';
import {
  Employee,
  TargetAudienceTypeEnum,
  Company,
  ManagementRole,
  departmentStringToFriendlyString,
} from '@employ-engage/data';
import MultiselectDropdownWithSearch from '../multiselect-dropdown/multiselect-dropdown-with-search';
import uniqBy from 'lodash.uniqby';
import { useTranslation } from 'react-i18next';

export interface TargetAudienceSelectorProps {
  individuals: Employee[];
  targetIndividuals: Employee[];
  onDeleteTargetIndividual: (
    list: ObjectPillListItem[],
    item: ObjectPillListItem
  ) => void;
  onAddTargetIndividual: (
    list: ObjectPillListItem[],
    item: ObjectPillListItem
  ) => void;
  managementRoles: ManagementRole[];
  targetManagementRoles: ManagementRole[];
  onDeleteTargetManagementRole: (
    list: ManagementRole[],
    item: ManagementRole
  ) => void;
  onAddTargetManagementRole: (item: ManagementRole) => void;
  companyData: Company;
  targetDepartments: string[];
  onDeleteTargetDepartment: (list: string[], item: string) => void;
  onAddTargetDepartment: (item: string) => void;
  targetDepartmentErrors: string | string[] | undefined;
  targetDepartmentTouched: boolean;
  targetIndividualsTestId?: string;
  targetManagementRolesTestId?: string;
  targetDepartmentsTestId?: string;
  refetchCompany: () => void;
}

export const TargetAudienceSelector = ({
  individuals,
  targetIndividuals,
  onDeleteTargetIndividual,
  onAddTargetIndividual,
  managementRoles,
  targetManagementRoles,
  onDeleteTargetManagementRole,
  onAddTargetManagementRole,
  companyData,
  targetDepartments,
  onDeleteTargetDepartment,
  onAddTargetDepartment,
  targetDepartmentErrors,
  targetDepartmentTouched,
  targetIndividualsTestId,
  targetManagementRolesTestId,
  targetDepartmentsTestId,
  refetchCompany,
}: TargetAudienceSelectorProps) => {
  const { t } = useTranslation(['common']);
  const pillListItemIndividuals: ObjectPillListItem[] = targetIndividuals.map(
    (item) => {
      return {
        label: `${item.employeeId} ${item.firstName} ${item.lastName} (${item.emailAddress})`,
        value: item.id,
      };
    }
  );

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-700">
        {t('Select at least one organization level, role or individual.')}
      </p>
      <div className={classNames('box-border col-span-2')}>
        <label
          htmlFor="targetManagementRoles"
          className="block text-sm font-medium text-gray-700"
        >
          {t(
            'Target Roles (optional - narrows the organization levels below; with no level, reaches everyone who holds the role)'
          )}
        </label>
        <div>
          <ObjectPillList
            testId={targetManagementRolesTestId}
            variant={TargetAudienceTypeEnum.ROLE}
            list={targetManagementRoles || []}
            onClickXMark={onDeleteTargetManagementRole}
            mapItem={(item) => ({
              key: item.id,
              label: item.name,
            })}
          />
          <ul>
            {managementRoles.map((item: ManagementRole) => {
              return (
                <li key={item.id} className="text-gray-500">
                  <span
                    className="cursor-pointer hover:text-gray-700"
                    onClick={() => onAddTargetManagementRole(item)}
                  >
                    {item.name}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <hr />
      <div className="col-span-2">
        <label
          htmlFor="targetDepartments"
          className="block text-sm font-medium text-gray-700"
        >
          {/*
            An earlier label said a selection here was required. Nothing
            enforced it, and an empty list meant "everyone" on the server — so
            choosing people by email address alone quietly targeted the whole
            company. A target now needs a level, a role or a person, and one
            that names nobody reaches nobody.
          */}
          {t('Target Organization Levels (optional)')}
        </label>
        <div>
          <ObjectPillList
            testId={targetDepartmentsTestId}
            variant={TargetAudienceTypeEnum.ORGANIZATION}
            list={targetDepartments || []}
            onClickXMark={onDeleteTargetDepartment}
            mapItem={(departmentId) => {
              const departmentName = departmentStringToFriendlyString(
                companyData,
                departmentId
              );
              return {
                key: departmentId,
                label: departmentName || departmentId,
              };
            }}
          />
          <OrgTreeSelector
            onSelectOrganization={onAddTargetDepartment}
            companyData={companyData}
            refetchCompany={refetchCompany}
          />
        </div>
        <ErrorMessage
          errors={targetDepartmentErrors}
          touched={targetDepartmentTouched}
        />
      </div>
      <hr />
      <div className={classNames('box-border col-span-2')}>
        <label
          htmlFor="targetIndividuals"
          className="block text-sm font-medium text-gray-700"
        >
          {t(
            'Target Individuals (optional - always included, in addition to anyone the levels and roles above reach)'
          )}
        </label>
        <div>
          <MultiselectDropdownWithSearch
            addEmptyOption={false}
            label=""
            selectedItems={pillListItemIndividuals}
            setSelectedItems={(items: ObjectPillListItem[]) => {
              const newItems = uniqBy(items, 'key');
              newItems.forEach((item) => {
                onAddTargetIndividual(newItems, item);
              });
            }}
            inputPlaceholder={t('000001 or John Doe or jon.doe@example.com')}
            options={individuals}
            mapOptions={(item: Employee) => {
              return {
                key: item.id,
                value: item.employeeId,
                label: `${item.employeeId} ${item.firstName} ${item.lastName} (${item.emailAddress})`,
              };
            }}
            onSearch={(query: string) => {
              return individuals.filter((item: Employee) => {
                return (
                  item.employeeId.toLowerCase().includes(query.toLowerCase()) ||
                  item.firstName.toLowerCase().includes(query.toLowerCase()) ||
                  item.lastName.toLowerCase().includes(query.toLowerCase()) ||
                  item.emailAddress.toLowerCase().includes(query.toLowerCase())
                );
              });
            }}
          />
          <ObjectPillList
            testId={targetIndividualsTestId}
            list={pillListItemIndividuals || []}
            onClickXMark={onDeleteTargetIndividual}
            variant={TargetAudienceTypeEnum.INDIVIDUAL}
            mapItem={(item) => {
              return {
                key: item.value,
                label: item.label,
              };
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TargetAudienceSelector;
